/**
 * Automatic Property Detection for HumanBehavior SDK
 * Captures device type, location, and initial referrer information
 */

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

export interface DeviceInfo {
    device_type: string;
    browser: string;
    browser_version: string;
    os: string;
    os_version: string;
    device_model?: string;
    screen_resolution: string;
    viewport_size: string;
    color_depth: number;
    timezone: string;
    language: string;
    languages: string[];
    raw_user_agent?: string;
}

export interface LocationInfo {
    current_url: string;
    pathname: string;
    search: string;
    hash: string;
    title: string;
    referrer: string;
    referrer_domain: string;
    initial_referrer: string;
    initial_referrer_domain: string;
    initial_host?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
}

export interface AutomaticProperties extends DeviceInfo, LocationInfo {}

/**
 * Detect device type based on user agent and screen size
 */
function detectDeviceType(): string {
    if (!isBrowser) return 'unknown';
    
    const userAgent = navigator.userAgent.toLowerCase();
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    
    // Mobile detection
    if (/mobile|android|iphone|ipad|ipod|blackberry|windows phone/i.test(userAgent)) {
        if (/ipad/i.test(userAgent) || (screenWidth >= 768 && screenHeight >= 1024)) {
            return 'tablet';
        }
        return 'mobile';
    }
    
    // Desktop detection
    if (/windows|macintosh|linux/i.test(userAgent)) {
        return 'desktop';
    }
    
    return 'unknown';
}

/**
 * Extract browser information from user agent
 */
function detectBrowser(): { browser: string; browser_version: string } {
    if (!isBrowser) return { browser: 'unknown', browser_version: 'unknown' };
    
    const userAgent = navigator.userAgent;
    
    // Chrome
    if (/chrome/i.test(userAgent) && !/edge/i.test(userAgent)) {
        const match = userAgent.match(/chrome\/(\d+)/i);
        return {
            browser: 'chrome',
            browser_version: match ? match[1] : 'unknown'
        };
    }
    
    // Firefox
    if (/firefox/i.test(userAgent)) {
        const match = userAgent.match(/firefox\/(\d+)/i);
        return {
            browser: 'firefox',
            browser_version: match ? match[1] : 'unknown'
        };
    }
    
    // Safari
    if (/safari/i.test(userAgent) && !/chrome/i.test(userAgent)) {
        const match = userAgent.match(/version\/(\d+)/i);
        return {
            browser: 'safari',
            browser_version: match ? match[1] : 'unknown'
        };
    }
    
    // Edge
    if (/edge/i.test(userAgent)) {
        const match = userAgent.match(/edge\/(\d+)/i);
        return {
            browser: 'edge',
            browser_version: match ? match[1] : 'unknown'
        };
    }
    
    // Internet Explorer
    if (/msie|trident/i.test(userAgent)) {
        const match = userAgent.match(/msie (\d+)/i) || userAgent.match(/rv:(\d+)/i);
        return {
            browser: 'ie',
            browser_version: match ? match[1] : 'unknown'
        };
    }
    
    return { browser: 'unknown', browser_version: 'unknown' };
}

/**
 * Extract operating system information from user agent
 */
function detectOS(): { os: string; os_version: string } {
    if (!isBrowser) return { os: 'unknown', os_version: 'unknown' };
    
    const userAgent = navigator.userAgent;
    
    // Windows
    if (/windows/i.test(userAgent)) {
        const match = userAgent.match(/windows nt (\d+\.\d+)/i);
        let version = 'unknown';
        if (match) {
            const versionNum = parseFloat(match[1]);
            if (versionNum === 10.0) version = '10';
            else if (versionNum === 6.3) version = '8.1';
            else if (versionNum === 6.2) version = '8';
            else if (versionNum === 6.1) version = '7';
            else version = match[1];
        }
        return { os: 'windows', os_version: version };
    }
    
    // iOS first. iPhone/iPad UAs also contain "like Mac OS X"; matching
    // Macintosh first labeled every iOS Safari session as macos.
    if (/iphone|ipad|ipod/i.test(userAgent)) {
        const match = userAgent.match(/os (\d+[._]\d+)/i);
        return {
            os: 'ios',
            os_version: match ? match[1].replace('_', '.') : 'unknown'
        };
    }

    // macOS
    if (/macintosh|mac os x/i.test(userAgent)) {
        const match = userAgent.match(/mac os x (\d+[._]\d+)/i);
        return {
            os: 'macos',
            os_version: match ? match[1].replace('_', '.') : 'unknown'
        };
    }
    
    // Android
    if (/android/i.test(userAgent)) {
        const match = userAgent.match(/android (\d+\.\d+)/i);
        return {
            os: 'android',
            os_version: match ? match[1] : 'unknown'
        };
    }
    
    // Linux
    if (/linux/i.test(userAgent)) {
        return { os: 'linux', os_version: 'unknown' };
    }
    
    return { os: 'unknown', os_version: 'unknown' };
}

/**
 * Extract UTM parameters from URL
 */
function extractUTMParams(url: string): Record<string, string> {
    const urlObj = new URL(url);
    const utmParams: Record<string, string> = {};
    
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    
    utmKeys.forEach(key => {
        const value = urlObj.searchParams.get(key);
        if (value) {
            utmParams[key] = value;
        }
    });
    
    return utmParams;
}

/**
 * Extract domain from URL
 */
function extractDomain(url: string): string {
    try {
        const urlObj = new URL(url);
        return urlObj.hostname;
    } catch {
        return '';
    }
}

/**
 * Get device information
 */
export function getDeviceInfo(): DeviceInfo {
    if (!isBrowser) {
        return {
            device_type: 'unknown',
            browser: 'unknown',
            browser_version: 'unknown',
            os: 'unknown',
            os_version: 'unknown',
            screen_resolution: 'unknown',
            viewport_size: 'unknown',
            color_depth: 0,
            timezone: 'unknown',
            language: 'unknown',
            languages: []
        };
    }
    
    const { browser, browser_version } = detectBrowser();
    const { os, os_version } = detectOS();
    
    return {
        device_type: detectDeviceType(),
        browser,
        browser_version,
        os,
        os_version,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        color_depth: window.screen.colorDepth,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        languages: [...(navigator.languages || [navigator.language])],
        raw_user_agent: navigator.userAgent
    };
}

/**
 * Get location information
 */
export function getLocationInfo(): LocationInfo {
    if (!isBrowser) {
        return {
            current_url: '',
            pathname: '',
            search: '',
            hash: '',
            title: '',
            referrer: '',
            referrer_domain: '',
            initial_referrer: '',
            initial_referrer_domain: ''
        };
    }
    
    const currentUrl = window.location.href;
    const referrer = document.referrer;
    const utmParams = extractUTMParams(currentUrl);
    
    return {
        current_url: currentUrl,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        title: document.title,
        referrer,
        referrer_domain: extractDomain(referrer),
        initial_referrer: referrer,
        initial_referrer_domain: extractDomain(referrer),
        initial_host: window.location.hostname,
        ...utmParams
    };
}

/**
 * Get all automatic properties
 */
export function getAutomaticProperties(): AutomaticProperties {
    return {
        ...getDeviceInfo(),
        ...getLocationInfo()
    };
}

/**
 * Get initial properties that should be captured once per session
 */
export function getInitialProperties(): Record<string, any> {
    if (!isBrowser) return {};
    
    const locationInfo = getLocationInfo();
    
    return {
        initial_referrer: locationInfo.initial_referrer,
        initial_referrer_domain: locationInfo.initial_referrer_domain,
        initial_url: locationInfo.current_url,
        initial_pathname: locationInfo.pathname,
        initial_utm_source: locationInfo.utm_source,
        initial_utm_medium: locationInfo.utm_medium,
        initial_utm_campaign: locationInfo.utm_campaign,
        initial_utm_term: locationInfo.utm_term,
        initial_utm_content: locationInfo.utm_content
    };
}

/**
 * Get current page properties (changes with navigation)
 */
export function getCurrentPageProperties(): Record<string, any> {
    if (!isBrowser) return {};
    
    const locationInfo = getLocationInfo();
    
    return {
        current_url: locationInfo.current_url,
        pathname: locationInfo.pathname,
        search: locationInfo.search,
        hash: locationInfo.hash,
        title: locationInfo.title,
        referrer: locationInfo.referrer,
        referrer_domain: locationInfo.referrer_domain,
        utm_source: locationInfo.utm_source,
        utm_medium: locationInfo.utm_medium,
        utm_campaign: locationInfo.utm_campaign,
        utm_term: locationInfo.utm_term,
        utm_content: locationInfo.utm_content
    };
}
